import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Gift, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { getCartItems } from "@/lib/sessionUtils";
import CartDrawer from "@/components/CartDrawer";
import ReorderModal from "@/components/ReorderModal";
import AccountMenu from "@/components/AccountMenu";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useOccasionTheme } from "@/hooks/useOccasionTheme";
import { useContactDetails } from "@/hooks/useContactDetails";
import { useLogoSettings } from "@/hooks/useLogoSettings";
import { useCollectionModuleStatus } from "@/hooks/useCollectionModule";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/hooks/useLanguage";
import { useMenuItems, MenuItem } from "@/hooks/useMenuItems";
import { useIsSalesmanOrAdmin } from "@/hooks/useWizardConfig";

// Official WhatsApp Icon
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    className={className}
    fill="currentColor"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Special page keys that have custom routes or actions
const SPECIAL_ROUTES: Record<string, string> = {
  "home": "/",
  "how-it-works": "/how-it-works",
  "blog": "/blog",
  "contact": "/contact",
  "returns-refunds": "/returns-refunds",
  "privacy-policy": "/privacy-policy",
  "terms-conditions": "/terms-conditions",
  "create-your-scent": "/quiz",
  "who-we-are": "/who-we-are",
  "origin-of-anaya": "/origin-of-anaya",
  "essence-of-anaya": "/essence-of-anaya",
  "mission-vision": "/mission-vision",
  "sustainability": "/sustainability",
  "special-occasions": "/special-occasions",
  "corporate-gifting": "/corporate-gifting",
  "anaya-collection": "/anaya-collection",
};

const getRouteForItem = (item: MenuItem): string => {
  if (item.cta_link) return item.cta_link;
  const special = SPECIAL_ROUTES[item.page_key];
  if (special) return special;
  // For dynamically created pages, use /page/:pageKey
  return `/page/${item.page_key}`;
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isReorderOpen, setIsReorderOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const { user, isAdmin, adminLoading } = useAuth();
  const { activeTheme } = useOccasionTheme();
  const { formatWhatsAppLink } = useContactDetails();
  const { logoSettings } = useLogoSettings();
  const { isModuleActive, isPreviewMode, loading: moduleLoading } = useCollectionModuleStatus();
  const { t, isArabicEnabled, language } = useLanguage();
  const { menuItems } = useMenuItems();
  const { allowed: canSeeSalesman } = useIsSalesmanOrAdmin(user?.id);

  const canShowCollectionCta = isModuleActive || (isPreviewMode && !adminLoading && !moduleLoading && isAdmin && !!user);

  const isValentine = activeTheme?.slug === "valentines";
  const isRamadan = activeTheme?.slug === "ramadan";
  
  const logoText = logoSettings?.logo_text || "ANAYA";
  const mobileFontSize = logoSettings?.font_size_mobile || "1.5rem";
  const desktopFontSize = logoSettings?.font_size_desktop || "1.875rem";

  useEffect(() => {
    const updateCartCount = () => {
      const cartItems = getCartItems();
      setCartCount(cartItems.length);
    };
    updateCartCount();
    const handleStorageChange = () => updateCartCount();
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [location]);

  useEffect(() => {
    setIsCartOpen(false);
    setIsMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleOpenCart = () => setIsCartOpen(true);
    window.addEventListener('openCartDrawer', handleOpenCart);
    return () => window.removeEventListener('openCartDrawer', handleOpenCart);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsMenuOpen(false);
    };
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const activeSection = menuItems.find((item) =>
      item.children?.some((child) => getRouteForItem(child) === location.pathname),
    );
    if (activeSection) {
      setExpandedSections((current) => ({ ...current, [activeSection.id]: true }));
    }
  }, [location.pathname, menuItems]);

  const toggleCart = () => {
    setIsCartOpen(prev => !prev);
  };

  const getItemLabel = (item: MenuItem) => {
    if (language === 'ar' && item.page_title_ar) return item.page_title_ar;
    return item.page_title;
  };

  const handleMenuItemClick = (item: MenuItem) => {
    if (item.page_key === "create-your-scent") {
      sessionStorage.setItem("startFreshQuiz", "true");
    }
    if (item.page_key === "home") {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
    setIsMenuOpen(false);
  };

  const renderMenuItem = (item: MenuItem) => {
    // Reorder perfume is a button action, not a link
    if (item.page_key === "reorder-perfume") {
      return (
        <button
          key={item.id}
          onClick={() => {
            setIsMenuOpen(false);
            setIsReorderOpen(true);
          }}
          className="font-body text-base py-1 text-foreground/70 hover:text-gold text-left rtl:text-right transition-colors"
        >
          {getItemLabel(item)}
        </button>
      );
    }

    return (
      <Link
        key={item.id}
        to={getRouteForItem(item)}
        className="font-body text-base py-1 text-foreground/70 hover:text-gold transition-colors"
        onClick={() => handleMenuItemClick(item)}
      >
        {getItemLabel(item)}
      </Link>
    );
  };

  const renderMenuSection = (item: MenuItem) => {
    const hasChildren = item.children && item.children.length > 0;

    // Sections with children render as grouped dropdowns
    if (hasChildren) {
      const isExpanded = expandedSections[item.id] ?? false;
      const sectionLabel = getItemLabel(item);
      return (
        <div key={item.id} className="border-b border-border/60 py-1 last:border-b-0">
          <Button
            type="button"
            variant="ghost"
            className="h-11 w-full justify-between px-2 font-body text-base font-medium text-gold hover:bg-gold/10 hover:text-gold"
            aria-expanded={isExpanded}
            aria-controls={`menu-section-${item.id}`}
            onClick={() => setExpandedSections((current) => ({
              ...current,
              [item.id]: !isExpanded,
            }))}
          >
            <span>{sectionLabel}</span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""}`}
              aria-hidden="true"
            />
          </Button>
          {isExpanded && (
            <div
              id={`menu-section-${item.id}`}
              className="mb-2 ms-3 flex flex-col border-s border-gold/30 ps-3"
            >
              {item.children?.map((child) => renderMenuItem(child))}
            </div>
          )}
        </div>
      );
    }

    // Top-level items without children render as direct links
    if (item.page_key === "reorder-perfume") {
      return (
        <button
          key={item.id}
          onClick={() => {
            setIsMenuOpen(false);
            setIsReorderOpen(true);
          }}
          className="font-body text-lg py-2 text-foreground/70 hover:text-gold text-left rtl:text-right transition-colors"
        >
          {getItemLabel(item)}
        </button>
      );
    }

    return (
      <Link
        key={item.id}
        to={getRouteForItem(item)}
        className="font-body text-lg py-2 text-foreground/70 hover:text-gold transition-colors"
        onClick={() => handleMenuItemClick(item)}
      >
        {getItemLabel(item)}
      </Link>
    );
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-strip-beige border-b border-border/50 pt-[var(--sat)]">
        <div className="container mx-auto h-14 flex items-center justify-between relative px-4 lg:px-8 xl:px-16">
          {/* Left: Menu Button + Logo */}
          <div className="flex items-center gap-2 -ms-2 h-10">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 hover:bg-accent rounded-md transition-colors h-10 w-10 flex items-center justify-center"
              aria-label={t("header.toggleMenu")}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            
            {!isValentine && !isRamadan && (
              <Link 
                to="/" 
                className="flex items-center gap-2 whitespace-nowrap h-10"
                onClick={() => {
                  setIsMenuOpen(false);
                  window.scrollTo({ top: 0, behavior: "instant" });
                }}
              >
                <span 
                  className="font-display text-gold tracking-wider leading-none"
                  style={{ fontSize: `clamp(${mobileFontSize}, 3vw, ${desktopFontSize})` }}
                >
                  {logoText}
                </span>
              </Link>
            )}
          </div>
          
          {/* Ramadan branding */}
          {isRamadan && (
            <div className="flex items-center gap-2 -ms-2">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 hover:bg-accent rounded-md transition-colors"
                aria-label={t("header.toggleMenu")}
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              <Link
                to="/"
                className="flex items-center gap-1 md:gap-2"
                onClick={() => {
                  setIsMenuOpen(false);
                  window.scrollTo({ top: 0, behavior: "instant" });
                }}
              >
                <span
                  className="font-display text-gold tracking-wider"
                  style={{ fontSize: `clamp(${mobileFontSize}, 3vw, ${desktopFontSize})` }}
                >
                  {logoText}
                </span>
                <span className="text-yellow-400 text-sm md:text-lg">🌙</span>
                <span className="flex flex-col md:flex-row md:gap-1 text-indigo-700 text-[10px] md:text-xs font-medium tracking-wide leading-tight">
                  <span>Ramadan</span>
                  <span>Mubarak</span>
                </span>
              </Link>
            </div>
          )}
          
          {/* Valentine branding */}
          {isValentine && (
            <div className="flex items-center gap-2 -ms-2">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 hover:bg-accent rounded-md transition-colors"
                aria-label={t("header.toggleMenu")}
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              <Link 
                to="/"
                className="flex items-center gap-1 md:gap-2"
                onClick={() => {
                  setIsMenuOpen(false);
                  window.scrollTo({ top: 0, behavior: "instant" });
                }}
              >
                <span 
                  className="font-display text-gold tracking-wider"
                  style={{ fontSize: `clamp(${mobileFontSize}, 3vw, ${desktopFontSize})` }}
                >
                  {logoText}
                </span>
                <span className="text-rose-500 text-[10px] md:text-xs lg:text-sm font-medium tracking-wide">
                  moments of love
                </span>
              </Link>
            </div>
          )}

          {/* Right: Language Switcher, Collection CTA, WhatsApp, Account, Cart */}
          <div className="flex items-center gap-2 -me-2">
            {isArabicEnabled && (
              <LanguageSwitcher variant="compact" />
            )}
            
            {canShowCollectionCta && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to="/anaya-collection"
                    className="inline-flex items-center justify-center gap-1.5 h-11 px-3 rounded-md bg-gold/10 hover:bg-gold/20 text-gold font-medium text-sm transition-colors"
                  >
                    <Gift className="h-7 w-7" />
                    <span className="hidden sm:inline">{t("nav.collection")}</span>
                  </Link>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isModuleActive ? t("tooltip.collection") : `${t("tooltip.collection")} (${t("common.preview")})`}</p>
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={formatWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center h-11 w-11 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
                  aria-label={t("tooltip.whatsapp")}
                >
                  <WhatsAppIcon className="h-7 w-7 text-[#25D366]" />
                </a>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("tooltip.whatsapp")}</p>
              </TooltipContent>
            </Tooltip>

            <AccountMenu onReorderClick={() => setIsReorderOpen(true)} />

            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="relative h-11 w-11 [&_svg]:size-7"
                  onClick={toggleCart}
                  aria-label={t("tooltip.cart")}
                >
                  <ShoppingBag className={`h-7 w-7 ${cartCount > 0 ? 'text-green-500' : ''}`} />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-gold text-gold-foreground text-xs font-semibold h-5 w-5 rounded-full flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("tooltip.cart")}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

      </header>

      {isMenuOpen && (
        <>
          <button
            type="button"
            className="fixed inset-x-0 bottom-0 top-[calc(3.5rem+var(--sat))] z-40 cursor-default bg-transparent"
            onClick={() => setIsMenuOpen(false)}
            aria-label={t("header.closeMenu")}
          />
          <aside
            className={`fixed bottom-0 top-[calc(3.5rem+var(--sat))] z-50 w-[85vw] max-w-[320px] overflow-y-auto overscroll-contain border-border bg-strip-beige shadow-xl animate-fade-in ${
              language === "ar" ? "right-0 border-l" : "left-0 border-r"
            }`}
            dir={language === "ar" ? "rtl" : "ltr"}
            aria-label={t("header.mainMenu")}
          >
            <nav className="flex min-h-full flex-col px-4 py-4">
              {isArabicEnabled && (
                <div className="mb-2 border-b border-border pb-3">
                  <LanguageSwitcher variant="minimal" />
                </div>
              )}

              <div className="flex flex-col">
                {menuItems.map((item) => renderMenuSection(item))}
              </div>

              {canSeeSalesman && (
                <div className="mt-4 border-t border-border pt-4">
                  <span className="font-body text-base font-medium text-gold">
                    {language === "ar" ? "مساحة عمل المبيعات" : "Salesman Workspace"}
                  </span>
                  <div className="mt-2 flex flex-col border-s border-gold/30 ps-3">
                    <Link
                      to="/salesman/perfume-wizard"
                      className="py-2 font-body text-base text-foreground/70 transition-colors hover:text-gold"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {language === "ar" ? "معالج العطور" : "Perfume Wizard"}
                    </Link>
                    <Link
                      to="/salesman/corporate-gifting-wizard"
                      className="py-2 font-body text-base text-foreground/70 transition-colors hover:text-gold"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {language === "ar" ? "معالج الهدايا العطرية للشركات" : "Corporate Gifting Wizard"}
                    </Link>
                    <Link
                      to="/salesman/orders"
                      className="py-2 font-body text-base text-foreground/70 transition-colors hover:text-gold"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {language === "ar" ? "طلباتي" : "My Orders"}
                    </Link>
                  </div>
                </div>
              )}
            </nav>
          </aside>
        </>
      )}

      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      <ReorderModal open={isReorderOpen} onOpenChange={setIsReorderOpen} />
    </>
  );
};

export default Header;
